import { inject, injectable } from "@launchtray/tsyringe-async";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { Renderer } from "pixi.js";
import { IAnalyticProviderWrapper } from "../../../analytics/IAnalyticProviderWrapper";
import { ICanvasScaler } from "../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../shared/async/PromiseCompletionSource";
import { Viewable } from "../../../scene/viewable/Viewable";
import { NotificationLayerProvider } from "../../composition/providers/NotificationLayerProvider";
import { IApplicationModel } from "../common/models/IApplicationModel";
import { IFirstTimeExperienceModel } from "../common/models/IFirstTimeExperienceModel";
import { IGuestModel } from "../common/models/IGuestModel";
import { IPlayerModel } from "../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../common/resources/CommonResourcesPackage";
import { MusicResourcePackageWrapper } from "../common/resources/MusicResourcePackageWrapper";
import { SoundsResourcePackageWrapper } from "../common/resources/SoundsResourcePackageWrapper";
import { TopPanel } from "./components/toppanel/TopPanel";
import { IGameController } from "./controllers/IGameController";
import { IGameModel } from "./models/IGameModel";
import { IGameViewModel } from "./models/IGameViewModel";
import { INewsModel } from "./models/INewsModel";
import { GameResourcePackage } from "./resources/GameResourcePackage";
import { NewsResourcePackage } from "./resources/NewsResourcePackage";
import { GameView } from "./views/GameView";
import { ILogger } from "../../logger/ILogger";
import { RenterPolicyModel } from "../common/models/Renters/RenterPolicyModel";
import { UserProfileResourcePackage } from "../popups/userprofile/resources/UserProfileResourcePackage";
import { ILatestNewsModel } from "../common/models/latestnews/ILatestNewsModel";
import { IConnectionModel } from "../../networking/IConnectionModel";
import { AsyncOperationNotification } from "../common/components/AsyncOperationNotification";
import { IFortuneWheelModel } from "../popups/hourlyBonus/model/IFortuneWheelModel";
import { GameInfo } from "../../../analytics/GameInfo";
import { IFortuneWheelController } from "../popups/hourlyBonus/mainHourlyBonus/controller/IFortuneWheelController";
import { ITournamentModel } from "../common/models/tournament/ITournamentModel";

@injectable()
export class GameScene extends Viewable<void> {
    public constructor(
        commonResources: CommonResourcesPackage,
        gameResources: GameResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        musicResources: MusicResourcePackageWrapper,
        newsResources: NewsResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        variableSaver: VariableSaver,
        @inject("IRenterPolicyModel") renterPolicyModel: RenterPolicyModel,
        @inject("ILatestNewsModel") latestNewsModel: ILatestNewsModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IApplicationModel") applicationModel: IApplicationModel,
        @inject("IGameModel") gameModel: IGameModel,
        @inject("IGameViewModel") gameViewModel: IGameViewModel,
        @inject("IGameController") gameController: IGameController,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("IAnalyticProviderWrapper") analyticsProvider: IAnalyticProviderWrapper,
        @inject("IFortuneWheelController") fortuneWheelController: IFortuneWheelController,
        @inject("IFortuneWheelModel") fortuneWheelModel: IFortuneWheelModel,
        @inject("INewsModel") newsModel: INewsModel,
        @inject("ITournamentModel") tournamentModel: ITournamentModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        renderer: Renderer,
        notificationLayerProvider: NotificationLayerProvider,
        topPanel: TopPanel,
        @inject("ILogger") logger: ILogger,
        asyncOperationNotification: AsyncOperationNotification,
        gameInfo: GameInfo
    ) {
        super();
        const gameViewCS = new PromiseCompletionSource<void>();
        this.view = new GameView(
            commonResources,
            gameResources,
            soundResources,
            musicResources,
            newsResources,
            userProfileResources,
            variableSaver,
            playerModel,
            guestModel,
            applicationModel,
            gameModel,
            gameViewModel,
            newsModel,
            gameController,
            fortuneWheelController,
            fortuneWheelModel,
            canvasScaler,
            analyticsProvider,
            gameViewCS,
            renderer,
            notificationLayerProvider,
            topPanel,
            firstTimeExperienceModel,
            connectionModel,
            logger,
            renterPolicyModel,
            latestNewsModel,
            tournamentModel,
            asyncOperationNotification,
            gameInfo
        );

        gameViewCS.result.then(async () => {
            this.operationControls.complete();
        }, (reason: any) => {
            this.operationControls.error(reason);
        });
    }
}
